/**
 * The app's frame cadence (home-ceo-mobile `frame-cadence.ts`), shared by
 * the water and the WEE story so both advance the same way.
 *
 * Paint at 30 fps within a 4 ms tolerance: at 60 Hz a strict 33.3 ms test
 * sits on a knife edge and jitters between 33 and 50 ms. Drop to 20 fps
 * while the frame interval's average runs above 40 ms, and come back once
 * it falls below 30.
 */

const FAST_MS = 1000 / 30;
const SLOW_MS = 50;
const TOLERANCE_MS = 4;
const SAMPLE_CAP_MS = 64;
/** A stall (a hidden tab, a long task) resumes where it was instead of jumping. */
const STEP_CAP_S = 0.1;

export type FrameCadence = {
  /**
   * Call once per animation frame. Returns the seconds to advance the
   * clock by when this frame should paint (0 on the first), or null when
   * it should be skipped.
   */
  tick(now: number): number | null;
  /** Forget the last frame, e.g. after the loop was stopped for a while. */
  reset(): void;
};

export function createFrameCadence(): FrameCadence {
  let lastPaint = 0;
  let lastFrame = 0;
  let average = 1000 / 60;
  let slow = false;

  return {
    tick(now) {
      if (lastFrame) {
        average += (Math.min(now - lastFrame, SAMPLE_CAP_MS) - average) * 0.1;
        if (!slow && average > 40) slow = true;
        else if (slow && average < 30) slow = false;
      }
      lastFrame = now;
      if (lastPaint && now - lastPaint < (slow ? SLOW_MS : FAST_MS) - TOLERANCE_MS) return null;
      const step = lastPaint ? Math.min((now - lastPaint) / 1000, STEP_CAP_S) : 0;
      lastPaint = now;
      return step;
    },
    reset() {
      lastPaint = 0;
      lastFrame = 0;
    },
  };
}
